import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * FLIP animation hook: registered elements glide to their new position
 * whenever a re-render moves them (e.g. rows re-sorting by votes).
 *
 * Usage:
 *   const setFlipRef = useFlipAnimation();
 *   <tr ref={setFlipRef(row.id)}>...</tr>
 */
export default function useFlipAnimation() {
  const nodes = useRef(new Map());
  const callbacks = useRef(new Map());
  const prevRects = useRef(new Map());

  const setFlipRef = useCallback((key) => {
    if (!callbacks.current.has(key)) {
      callbacks.current.set(key, (el) => {
        if (el) nodes.current.set(key, el);
        else nodes.current.delete(key);
      });
    }
    return callbacks.current.get(key);
  }, []);

  useLayoutEffect(() => {
    const rects = new Map();
    nodes.current.forEach((el, key) => rects.set(key, el.getBoundingClientRect()));

    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      rects.forEach((rect, key) => {
        const prev = prevRects.current.get(key);
        const el = nodes.current.get(key);
        if (!prev || !el) return;
        const dx = prev.left - rect.left;
        const dy = prev.top - rect.top;
        if (Math.abs(dy) < 1 && Math.abs(dx) < 1) return;
        // Invert: jump back to the old position without transitioning...
        el.style.transition = 'none';
        el.style.transform = `translate(${dx}px, ${dy}px)`;
        // ...then play: release to the new position on the next frame.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.style.transition = 'transform 400ms cubic-bezier(0.22, 1, 0.36, 1)';
            el.style.transform = '';
          });
        });
      });
    }

    prevRects.current = rects;
  });

  return setFlipRef;
}
